import api from "../../api/axios";
import Input from "../ui/Input";
import Button from "../ui/Button";
import React, { useEffect, useRef, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import ModelSelect from "../ui/ModelSelect";

const ProductionChainSortie = () => {
  const [models, setModels] = useState([]);
  const [chains, setChains] = useState([]);
  const [posts, setPosts] = useState([]);
  const [encour, setEncour] = useState(0);
  const [selectedModel, setSelectedModel] = useState("");
  const [selectedChain, setSelectedChain] = useState("");
  const [selectedPost, setSelectedPost] = useState("");

  const [sortie, setSortie] = useState(0);
  const [retouch, setRetouch] = useState(0);
  const [userRole, setUserRole] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [isLoadingData, setIsLoadingData] = useState(false);
  const sortieSaveTimer = useRef(null);
  const lastSavedSortie = useRef(0);
  const scannerInput = useRef(null);
  const scannedEpcs = useRef(new Set());
  const [scannedBarcode, setScannedBarcode] = useState("");
  const [pendingScanCount, setPendingScanCount] = useState(0);
  const [lastScan, setLastScan] = useState(null);
  const [cancelBarcode, setCancelBarcode] = useState("");
  const [isCancellingScan, setIsCancellingScan] = useState(false);

  const hasAdminSortieFeatures = [
    "admin",
    "superadmin",
    "developer",
    "manager",
    "Chaîne_production_sortie",
  ].includes(userRole);

  useEffect(() => {
    setUserRole(localStorage.getItem("role") || "");
  }, []);


  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };


  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await api.get("/models");
        setModels(response.data);
      } catch (error) {
        console.error("Error fetching models:", error);
      }
    };

    const fetchChains = async () => {
      try {
        const response = await api.get("/chains");
        setChains(response.data);
      } catch (error) {
        console.error("Error fetching chains:", error);
      }
    };

    const fetchPosts = async (selectedModel) => {
      try {
        const response = await api.get("/posts", {
          params: { model_id: selectedModel },
        });
        setPosts(response.data);
      } catch (error) {
        console.error("Error fetching posts:", error);
      }
    };

    fetchModels();
    fetchChains();
    if(selectedModel) {
      fetchPosts(selectedModel);
    }
  }, [selectedModel]);

  useEffect(() => {
    const fetchProductionData = async () => {
      if (selectedModel && selectedChain) {
        setIsLoadingData(true);
        try {
          const datePath = hasAdminSortieFeatures && selectedDate
            ? `/${selectedDate}`
            : "";
          const response = await api.get(
            `/chain_production_sortie/${selectedModel}/${selectedChain}${datePath}`
          );

          const { entre, sortie , sum_retouch } = response.data;
          if (hasAdminSortieFeatures) {
            if (sortie == null) {
              setSortie(0);
            } else {
              setSortie(sortie);
              lastSavedSortie.current = Number(sortie);
            }
          } else {
            if (sum_retouch == null) {
              setSortie(0);
            } else {
              setSortie(sum_retouch);
              lastSavedSortie.current = Number(sum_retouch);
            }
          }
          setEncour(entre);
        } catch (error) {
          console.error("Error fetching production data:", error);
        } finally {
          setIsLoadingData(false);
        }
      }
    };

    fetchProductionData();
  }, [selectedModel, selectedChain, selectedDate, hasAdminSortieFeatures]);

  useEffect(() => () => {
    if (sortieSaveTimer.current) clearTimeout(sortieSaveTimer.current);
  }, []);

  const saveSortie = async (value) => {
    try {
      const datePath = hasAdminSortieFeatures && selectedDate
        ? `/${selectedDate}`
        : "";
      await api.post(`/chain_production/${selectedModel}/${selectedChain}${datePath}`, {
        sortie: value,
        recorded_local_time: new Date().toTimeString().slice(0, 8),
      });
      lastSavedSortie.current = value;
    } catch (error) {
      console.error("Error updating production data:", error);
      toast.error(error.response?.data?.error || "Error updating production data");
      setSortie(lastSavedSortie.current);
    } finally {
      setIsButtonDisabled(false);
    }
  };

  const scheduleSortieSave = (value) => {
    if (sortieSaveTimer.current) clearTimeout(sortieSaveTimer.current);
    sortieSaveTimer.current = setTimeout(() => saveSortie(value), 500);
  };

  const handleModelChange = (e) => {
    setSelectedModel(e.target.value);
  };

  const handleChainChange = (e) => {
    setSelectedChain(e.target.value);
    setTimeout(() => scannerInput.current?.focus(), 0);
  };

  const handlePieceScan = async (event) => {
    event.preventDefault();
    const barcode = scannedBarcode.trim();
    if (!selectedModel || !selectedChain) {
      toast.error("Sélectionnez d'abord un modèle et une chaîne.");
      return;
    }
    if (!barcode) return;

    // UHF readers can emit the same tag many times while it remains in range.
    // Claim the normalized EPC before awaiting the API so even rapid duplicate
    // events are ignored, while a different EPC can be submitted immediately.
    const normalizedEpc = barcode.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (!normalizedEpc || scannedEpcs.current.has(normalizedEpc)) {
      setScannedBarcode("");
      setTimeout(() => scannerInput.current?.focus(), 0);
      return;
    }
    scannedEpcs.current.add(normalizedEpc);
    setScannedBarcode("");

    setPendingScanCount((count) => count + 1);
    try {
      const response = await api.post("/scanner/chain-sortie", {
        barcode,
        model_id: Number(selectedModel),
        chain_id: Number(selectedChain),
      }, { headers: { "Content-Type": "application/json" } });
      const data = response.data;
      setSelectedDate(data.production_date);
      setSortie(data.total_sortie);
      lastSavedSortie.current = data.total_sortie;
      setEncour(data.remaining);
      setLastScan({ ok: true, barcode, model: data.model.modele, date: data.production_date });
      toast.success(`+1 sortie — ${data.model.modele}`);
    } catch (error) {
      const message = error.response?.data?.error || "Impossible d'enregistrer ce scan.";
      setLastScan({ ok: false, barcode, message });
      toast.error(message);
    } finally {
      setPendingScanCount((count) => Math.max(0, count - 1));
      setTimeout(() => scannerInput.current?.focus(), 0);
    }
  };

  const handleCancelScan = async (event) => {
    event.preventDefault();
    const barcode = cancelBarcode.trim();
    if (!selectedChain) {
      toast.error("Sélectionnez d'abord la chaîne concernée.");
      return;
    }
    if (!barcode || isCancellingScan) return;

    setIsCancellingScan(true);
    try {
      const response = await api.post("/scanner/chain-sortie/cancel", {
        barcode,
        chain_id: Number(selectedChain),
      }, { headers: { "Content-Type": "application/json" } });
      const data = response.data;
      setSelectedModel(String(data.model.id));
      setSelectedDate(data.production_date);
      setSortie(data.daily_sortie);
      lastSavedSortie.current = data.daily_sortie;
      scannedEpcs.current.delete(barcode.toUpperCase().replace(/[^A-Z0-9]/g, ""));
      setCancelBarcode("");
      setLastScan({ ok: true, cancelled: true, barcode, model: data.model.modele, date: data.production_date });
      toast.success(`Scan annulé — ${data.model.modele} — sortie -1`);
    } catch (error) {
      const message = error.response?.data?.error || "Impossible d'annuler ce scan.";
      setLastScan({ ok: false, barcode, message });
      toast.error(message);
    } finally {
      setIsCancellingScan(false);
    }
  };

  const handlePostChange = (e) => {
    setSelectedPost(e.target.value);
  };

  const onChange = async (name, value) => {
    // Don't submit if we're currently loading data
    if (isLoadingData) {
      return;
    }

    const intValue = parseInt(value, 10) || 0; 

    if (intValue < 0) {
      toast.error("Value cannot be less than 0");
      return;
    }


  
    if (name === "sortie") {
      setSortie(intValue);
      scheduleSortieSave(intValue);
  
    } else if (name === "retouch") {
      setRetouch(intValue);
    }
  };
  
  const handleChange = async (direction, type) => {
    // Don't submit if we're currently loading data
    if (isLoadingData) {
      return;
    }

    let newValue;
    if (type === "sortie") {
      newValue = direction === "next" ? parseInt(sortie) + 1 : parseInt(sortie) - 1;
      if (newValue < 0) newValue = 0;
      setSortie(newValue);
      scheduleSortieSave(newValue);
    } else if (type === "retouch") {
      newValue =
        direction === "next" ? parseInt(retouch) + 1 : parseInt(retouch) - 1;
      if (newValue < 0) newValue = 0;
      setRetouch(newValue);
    }
  };

  const handleRetouchSubmit = async () => {
    if (!selectedPost) {
      toast.error("Please select a post before submitting retouch.");
      return;
    }

    try {
      await api.post(`/retouch`, {
        chain_id: selectedChain,
        model_id: selectedModel,
        value: retouch,
        post_id: selectedPost,
      });

      setRetouch(0);
      setSelectedPost("");
    } catch (error) {
      console.error("Error updating retouch data:", error);
    } finally {
      setIsButtonDisabled(false);
    }
  };

  const selectedModelData = models.find(
    (model) => String(model.id) === String(selectedModel)
  );

  return (
    <div className="ml-[19%] pt-[6rem]">
      <ToastContainer />
      
      {/* Header Section */}
      <div className="bg-white shadow-sm border-b border-gray-200 mb-6">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Production Chain</h2>
              <p className="text-sm text-gray-600 mt-1">Manage production data and sortie tracking</p>
            </div>
          </div>
        </div>
      </div>

      {/* Controls Section */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 mb-6">
        <div className="px-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Date Selector */}
            {hasAdminSortieFeatures && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Select Date
                </label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                />
              </div>
            )}

            {/* Model Selector */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Model
              </label>
              <ModelSelect value={selectedModel} onChange={handleModelChange} models={models} />
            </div>

            {/* Chain Selector */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Chain
              </label>
              <select
                value={selectedChain}
                onChange={handleChainChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
              >
                <option value="">Select Chain</option>
                {chains.map((chain, index) => (
                  <option key={index} value={chain.id}>
                    {chain.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Indicator */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Status
              </label>
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${selectedChain ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                <span className="text-sm text-gray-600">
                  {selectedModel && selectedChain ? 'Scanner prêt' : 'Sélectionnez un modèle et une chaîne'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Piece scanner: the selected model and chain determine the destination. */}
      <div className="bg-slate-900 text-white shadow-sm rounded-lg border border-slate-700 mb-6">
        <div className="px-6 py-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
            <div className="flex-1">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-bold">Scanner une pièce</h3>
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-bold ${selectedModel && selectedChain ? "bg-emerald-500 text-white" : "bg-slate-700 text-slate-300"}`}>{selectedModel && selectedChain ? "PRÊT" : "MODÈLE ET CHAÎNE REQUIS"}</span>
              </div>
              <form onSubmit={handlePieceScan} className="mt-4 flex gap-2">
                <input
                  ref={scannerInput}
                  value={scannedBarcode}
                  onChange={(event) => setScannedBarcode(event.target.value)}
                  disabled={!selectedModel || !selectedChain}
                  autoComplete="off"
                  placeholder={selectedModel && selectedChain ? "Scannez le code-barres unique de la pièce…" : "Sélectionnez d'abord un modèle et une chaîne"}
                  className="min-w-0 flex-1 rounded-lg border-2 border-slate-600 bg-white px-4 py-3 text-lg font-semibold tracking-wide text-slate-900 outline-none focus:border-blue-400 disabled:bg-slate-200"
                />
                <button
                  type="submit"
                  disabled={!selectedModel || !selectedChain || !scannedBarcode.trim()}
                  className="rounded-lg bg-blue-600 px-6 font-bold hover:bg-blue-500 disabled:cursor-not-allowed disabled:bg-slate-600"
                >
                  {pendingScanCount > 0 ? `Validation… (${pendingScanCount})` : "Valider"}
                </button>
              </form>
              {lastScan && <div className={`mt-3 rounded-lg px-4 py-3 text-sm ${lastScan.ok ? "bg-emerald-900/60 text-emerald-100" : "bg-red-900/60 text-red-100"}`}>
                {lastScan.ok ? (lastScan.cancelled ? <><strong>Scan annulé :</strong> {lastScan.barcode} → modèle <strong>{lastScan.model}</strong>, sortie -1.</> : <><strong>Scan accepté :</strong> {lastScan.barcode} → modèle <strong>{lastScan.model}</strong>, date {lastScan.date}, sortie +1.</>) : <><strong>Opération refusée :</strong> {lastScan.message}</>}
              </div>}

              <div className="mt-5 border-t border-slate-700 pt-5">
                <h4 className="font-bold text-red-300">Annuler une pièce scannée</h4>
                <p className="mt-1 text-xs text-slate-400">Saisissez ou scannez le numéro complet de la pièce, puis confirmez l'annulation. Le code restera enregistré et ne pourra pas être rescanné.</p>
                <form onSubmit={handleCancelScan} className="mt-3 flex gap-2">
                  <input
                    value={cancelBarcode}
                    onChange={(event) => setCancelBarcode(event.target.value)}
                    disabled={!selectedChain || isCancellingScan}
                    autoComplete="off"
                    placeholder="Numéro complet de la pièce"
                    className="min-w-0 flex-1 rounded-lg border border-slate-600 bg-white px-4 py-3 font-semibold tracking-wide text-slate-900 outline-none focus:border-red-400 disabled:bg-slate-200"
                  />
                  <button disabled={!selectedChain || isCancellingScan || !cancelBarcode.trim()} className="rounded-lg bg-red-600 px-6 font-bold hover:bg-red-500 disabled:cursor-not-allowed disabled:bg-slate-600">
                    {isCancellingScan ? "Annulation…" : "Annuler"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Model Information Section */}
      {selectedModelData && selectedChain && (
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 mb-6">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedModelData.category}
                </h3>
                <p className="text-sm text-gray-600">Category</p>
              </div>
              <div className="text-right">
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedModelData.client}
                </h3>
                <p className="text-sm text-gray-600">Client</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-semibold">E</span>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-600">Encour</p>
                    <p className="text-2xl font-bold text-blue-600">{encour}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-semibold">T</span>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-600">Total Sortie</p>
                    <p className="text-2xl font-bold text-green-600">{sortie}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="flex justify-between items-center">
        {selectedModel && selectedChain && (
          <div className="flex justify-center mx-auto mt-[2rem] items-center mb-4 shadow-md w-fit p-3 rounded border">
            <button
              className="font-semibold text-[18px] bg-red-300 hover:bg-red-400 p-2 transition duration-300 rounded mt-9 w-[70px] border border-black m-[12px] mt-4"
              onClick={() => handleChange("prev", "sortie")}
              disabled={isButtonDisabled}
            >
              -
            </button>
            <Input
              handleChange={onChange}
              name={"sortie"}
              placeholder="Sortie"
              label={"Sortie"}
              text={sortie}
            />
            <button
              className="font-semibold text-[18px] bg-green-300 hover:bg-green-400 p-2 transition duration-300 rounded mt-9 w-[70px] border border-black m-[12px] mt-4"
              onClick={() => handleChange("next", "sortie")}
              disabled={isButtonDisabled}
            >
              +
            </button>
          </div>
        )}
        {selectedModel && selectedChain && (
          <div className="flex justify-center mx-auto mt-[2rem] items-center mb-4 shadow-md w-fit p-3 rounded border">
            <button
              className="font-semibold text-[18px] bg-red-300 hover:bg-red-400 p-2 transition duration-300 rounded mt-9 w-[70px] border border-black m-[12px] mt-4"
              onClick={() => handleChange("prev", "retouch")}
              disabled={isButtonDisabled}
            >
              -
            </button>
            <Input
              handleChange={onChange}
              name={"retouch"}
              placeholder="Retouch"
              label={"Retouch"}
              text={retouch}
            />
            <button
              className="font-semibold text-[18px] bg-green-300 hover:bg-green-400 p-2 transition duration-300 rounded mt-9 w-[70px] border border-black m-[12px] mt-4"
              onClick={() => handleChange("next", "retouch")}
              disabled={isButtonDisabled}
            >
              +
            </button>
            <div>
              <label htmlFor="sleect" className="block font-semibold opacity-0">
                .
              </label>
              <select
                id="sleect"
                className="block w-fit mt-4 mr-4 outline-0 p-[.5rem] border border-[#b3b3b3] focus:border-2 focus:border-[#2684ff] rounded"
                value={selectedPost}
                onChange={handlePostChange}
                disabled={retouch === 0}
              >
                <option value="">Select Post</option>
                {posts.map((post, index) => (
                  <option key={index} value={post.id}>
                    {post.name}
                  </option>
                ))}
              </select>
            </div>
            <Button
              classes={"bg-blue-500 mt-10"}
              handlePress={handleRetouchSubmit}
              disabled={isButtonDisabled}
            >
              Submit
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductionChainSortie;
